import { useState, useRef } from 'react';
import { useT } from '../../i18n';

interface FileUploadProps {
  expectedHeaders: string[];
  onUpload: (data: any[]) => void;
  onCancel: () => void;
}

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  let i = 0;

  while (i < line.length) {
    const char = line[i];

    if (inQuotes) {
      if (char === '"') {
        // Check for escaped quote (doubled quote)
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i += 2;
          continue;
        }
        // End of quoted field
        inQuotes = false;
        i++;
        continue;
      }
      current += char;
      i++;
    } else {
      if (char === '"') {
        inQuotes = true;
        i++;
        continue;
      }
      if (char === ',') {
        result.push(current.trim());
        current = '';
        i++;
        continue;
      }
      current += char;
      i++;
    }
  }

  // Don't forget the last field
  result.push(current.trim());

  return result;
}

function parseCSV(text: string): { headers: string[]; rows: any[] } {
  // Normalize line endings
  const normalizedText = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();

  // Split into lines while respecting quoted multi-line fields
  const lines: string[] = [];
  let currentLine = '';
  let inQuotes = false;

  for (let i = 0; i < normalizedText.length; i++) {
    const char = normalizedText[i];

    if (char === '"') {
      if (inQuotes && i + 1 < normalizedText.length && normalizedText[i + 1] === '"') {
        // Escaped quote - add both and skip next
        currentLine += '""';
        i++;
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
        currentLine += char;
      }
    } else if (char === '\n' && !inQuotes) {
      // End of line (not in quotes)
      if (currentLine.trim()) {
        lines.push(currentLine);
      }
      currentLine = '';
    } else {
      currentLine += char;
    }
  }

  // Don't forget the last line
  if (currentLine.trim()) {
    lines.push(currentLine);
  }

  if (lines.length === 0) return { headers: [], rows: [] };

  // Parse header
  const headers = parseCSVLine(lines[0]);

  // Parse rows
  const rows: Record<string, string>[] = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue; // Skip empty lines

    const values = parseCSVLine(line);
    const row: Record<string, string> = {};
    headers.forEach((header, idx) => {
      row[header] = values[idx] || '';
    });
    rows.push(row);
  }

  return { headers, rows };
}

export function FileUpload({ expectedHeaders, onUpload, onCancel }: FileUploadProps) {
  const { t } = useT();
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<any[] | null>(null);
  const [error, setError] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = (f: File) => {
    setError('');
    setFile(f);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const { headers, rows } = parseCSV(text);

      // Validate headers
      const missingHeaders = expectedHeaders.filter((h) => !headers.includes(h));
      if (missingHeaders.length > 0) {
        setError(t('file.missingHeaders').replace('{headers}', missingHeaders.join(', ')));
        setPreview(null);
        return;
      }

      setPreview(rows);
    };
    reader.readAsText(f);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f && f.name.endsWith('.csv')) {
      handleFile(f);
    } else {
      setError(t('file.notCsv'));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
  };

  return (
    <div>
      <div
        className={`file-upload ${dragging ? 'dragging' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <input type="file" ref={inputRef} accept=".csv" onChange={handleChange} style={{ display: 'none' }} />
        <p>
          {file ? file.name : t('file.dropHint')}
        </p>
        <p className="text-xs text-muted">
          {t('file.expectedHeaders').replace('{headers}', expectedHeaders.join(', '))}
        </p>
      </div>

      {error && (
        <div className="badge badge-err mt-12" style={{ display: 'block' }}>
          {error}
        </div>
      )}

      {preview && (
        <div className="mt-12">
          <p className="text-sm font-medium mb-8">{t('file.preview').replace('{n}', String(preview.length))}</p>
          <div style={{ maxHeight: 200, overflow: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  {expectedHeaders.map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.slice(0, 5).map((row, i) => (
                  <tr key={i}>
                    {expectedHeaders.map((h) => (
                      <td key={h}>{row[h]?.slice(0, 50)}</td>
                    ))}
                  </tr>
                ))}
                {preview.length > 5 && (
                  <tr>
                    <td colSpan={expectedHeaders.length} className="text-muted text-center">
                      {t('file.moreRows').replace('{n}', String(preview.length - 5))}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="modal-footer" style={{ padding: 0, border: 'none', marginTop: 'var(--s16)' }}>
        <button type="button" className="btn btn-ghost" onClick={onCancel}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="btn btn-accent"
          disabled={!preview || preview.length === 0}
          onClick={() => preview && onUpload(preview)}
        >
          {t('file.uploadRows').replace('{n}', String(preview?.length || 0))}
        </button>
      </div>
    </div>
  );
}
